import { renderToStaticMarkup } from "react-dom/server";
import _, {
  upperFirst,
  toLower,
  groupBy,
  map,
  last,
  find,
  some,
  memoize,
  omitBy,
  isUndefined,
} from "lodash";

import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import moment from "moment-timezone";

import {
  DEFAULT_LANGUAGE,
  ETransactionType,
  ETransactionTypes,
  FILE_STORAGE_SERVER_URL,
  INVOICE_NOTES,
} from "./constants";
import JSON_COUNTRY_INFO from "../json/countries.json";
import { sortBy, take, isObject, isString, get, isEqual } from "lodash-es";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import apiClient from "./api-client";
import { transactionStatus } from "./transactions";
import toast from "react-hot-toast";
import i18n from "../i18n";
import { ERecurringTransactionFrequency } from "src/components/RecurringTransaction/RecurringTransactionSettings";
import { EContactType } from "src/pages/Sources/AddContactDetailsModal";
import { createElement, HTMLAttributes, ReactElement, ReactNode } from "react";
import { MdCheck, MdClose } from "react-icons/md";
import { ALL_TRANSACTION_STATUSES } from "gastos-shared";
import { API_ENDPOINT_GET_INVOICE } from "./endpoint-urls";
import {
  FILE_TYPE_IMG,
  FILE_TYPE_PDF,
  FILE_TYPE_XML,
  SUPPORTED_FILE_TYPES,
} from "src/constants/file-types";
import { ICON_SIZES } from "src/constants/icons";

export const formatNumber = memoize((value: string | number) => {
  return (
    new Intl.NumberFormat("default", {
      maximumFractionDigits: 2,
    })?.format?.(Number(value)) || value
  );
});

const EXCLUDED_WORDS: Record<string, string[]> = {
  en: [
    "a",
    "an",
    "the",
    "and",
    "but",
    "or",
    "nor",
    "for",
    "so",
    "yet",
    "at",
    "by",
    "in",
    "of",
    "on",
    "to",
    "up",
    "with",
  ],
  es: [
    "el",
    "la",
    "los",
    "las",
    "un",
    "una",
    "unos",
    "unas",
    "de",
    "del",
    "al",
    "en",
    "con",
    "por",
    "para",
    "sin",
    "sobre",
    "entre",
    "y",
    "o",
    "u",
    "ni",
    "que",
    "a",
  ],
  pt: [
    "o",
    "a",
    "os",
    "as",
    "um",
    "uma",
    "uns",
    "umas",
    "de",
    "do",
    "da",
    "dos",
    "das",
    "em",
    "no",
    "na",
    "nos",
    "nas",
    "por",
    "para",
    "com",
    "sem",
    "sob",
    "sobre",
    "e",
    "ou",
    "que",
  ],
  zh: [],
};

export const currentLanguage = () =>
  i18n.language?.split("-")[0] || DEFAULT_LANGUAGE;

export function capitalizeTitle(text: string): string {
  const lang = currentLanguage();
  const excludedWords = EXCLUDED_WORDS[lang] || [];

  if (!text) return "";

  if (lang === "zh") return text;

  const words = toLower(text).split(" ");

  return words
    .map((word, index) => {
      const isFirst = index === 0;
      const isLast = index === words.length - 1;

      if (isFirst || isLast || !excludedWords.includes(word)) {
        return upperFirst(word);
      }

      return word;
    })
    .join(" ");
}

export const formatCurrency = memoize(
  ({ value, currency }: { value: string | number; currency: string }) => {
    const absNumber = Math.abs(Number(value));
    const negativeSymbol = Number(value) < 0 ? "-" : "";

    return `${negativeSymbol}${currency}${formatNumber(absNumber)}`;
  },
);

export const getRandomColor = (alpha = 1) => {
  const [r, g, b, a] = [
    Math.floor(Math.random() * 256),
    Math.floor(Math.random() * 256),
    Math.floor(Math.random() * 256),
    alpha,
  ];

  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

export const getRandomRgbaColor = (alpha = 1) => {
  const [r, g, b, a] = [
    Math.floor(Math.random() * 256),
    Math.floor(Math.random() * 256),
    Math.floor(Math.random() * 256),
    alpha,
  ];

  return { r, g, b, a };
};

const getLuminance = ({
  r,
  g,
  b,
  a,
}: {
  r: number;
  g: number;
  b: number;
  a: number;
}) => {
  const toLinear = (c: number) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
};

const getContrastRatio = (
  color1: { r: number; g: number; b: number; a: number },
  color2: { r: number; g: number; b: number; a: number },
) => {
  const L1 = getLuminance(color1);
  const L2 = getLuminance(color2);
  const brightest = Math.max(L1, L2);
  const darkest = Math.min(L1, L2);
  return (brightest + 0.05) / (darkest + 0.05);
};

export const getContrastingRandomRgbaColor = ({
  r,
  g,
  b,
  a,
}: {
  r: number;
  g: number;
  b: number;
  a: number;
}) => {
  const minContrast = 4.5;
  let textColor;
  let tries = 0;

  do {
    textColor = getRandomRgbaColor(a);
    tries++;
  } while (
    getContrastRatio({ r, g, b, a }, textColor) < minContrast &&
    tries < 100
  );

  return textColor;
};

export const transformToSelectOptions = memoize(
  ({
    data,
    labelField,
    valueField,
  }: {
    data: Array<any>;
    labelField: string | any;
    valueField: string | any;
  }) => {
    if (data?.length) {
      return sortBy(
        data.map((option) => ({
          value: option[valueField],
          label: option[labelField],
        })),
        ["value"],
      );
    }

    return [];
  },
);

export const findInArrayOfObjects = memoize(
  ({
    data,
    fieldToCompare,
    valueToFind,
  }: {
    data: Array<any>;
    fieldToCompare: string;
    valueToFind: string;
  }) => {
    return data?.find?.((row) => row[fieldToCompare] === valueToFind) || null;
  },
);

export const existsInArrayOfObjects = memoize(
  ({
    data,
    fieldToCompare,
    valueToFind,
  }: {
    data: Array<any>;
    fieldToCompare: string;
    valueToFind: string;
  }) => {
    if (data?.length) {
      const exists = data.find?.(
        (row: any) => row?.[fieldToCompare] === valueToFind,
      );

      return !!exists;
    }

    return false;
  },
);

export const uniqueList = memoize(
  ({ data, referenceField }: { data: Array<any>; referenceField: string }) => {
    if (data?.length) {
      const res: Array<any> = [];

      data.forEach?.((row: any) => {
        if (
          !existsInArrayOfObjects({
            data: res,
            fieldToCompare: referenceField,
            valueToFind: row[referenceField],
          })
        ) {
          res.push(row);
        }
      });
    }

    return [];
  },
);

export const getDaysBetweenDates = memoize(function ({
  startDate,
  endDate,
}: {
  startDate: any;
  endDate: any;
}) {
  var dates: any[] = [];

  var currDate = moment(startDate).startOf("day");
  var lastDate = moment(endDate).startOf("day");

  while (currDate.add(1, "days").diff(lastDate) < 0) {
    dates.push(currDate.clone().toDate());
  }

  return dates;
});

export const getDomainName = (url: string) => {
  try {
    const { hostname } = new URL(url);

    if (hostname) {
      if (hostname.toLowerCase().startsWith("www.")) {
        return hostname.replace("wwww.", "");
      }

      return hostname;
    }

    return null;
  } catch (_e) {
    return null;
  }
};

export const isFullscreen = document?.fullscreenElement;

export const toggleFullscreen = () => {
  let elem: any = document.documentElement;
  const isFullscreen = document?.fullscreenElement;

  if (isFullscreen) {
    document.exitFullscreen();
  } else {
    elem
      .requestFullscreen({ navigationUI: "show" })
      .then(() => {})
      .catch((err: any) => {
        alert(
          `An error occurred while trying to switch into fullscreen mode: ${err.message} (${err.name})`,
        );
      });
  }
};

export const hasFullscreenSupport = () => {
  return !!document?.fullscreenEnabled;
};

export const getFileTypeByExtension = (filename: string) => {
  const theFilename = `${filename}`.toLowerCase?.();

  if (
    theFilename.endsWith(".jpg") ||
    theFilename.endsWith(".png") ||
    theFilename.endsWith(".jpeg") ||
    theFilename.endsWith(".webp")
  ) {
    return FILE_TYPE_IMG;
  }

  if (theFilename.endsWith(".xml")) {
    return FILE_TYPE_XML;
  }

  if (theFilename.endsWith(".pdf")) {
    return FILE_TYPE_PDF;
  }
};

// export const CURRENCY_SELECT_OPTIONS = sortBy(
//   transformToSelectOptions({
//     data: JSON_CURRENCY_INFO,
//     labelField: "name",
//     valueField: "code",
//   }),
//   ["label"]
// );

export const COUNTRY_SELECT_OPTIONS = sortBy(
  transformToSelectOptions({
    data: JSON_COUNTRY_INFO,
    labelField: "name",
    valueField: "code",
  }),
  ["label"],
);

// export const getCurrencyDataByCode = ({ code }: { code: string }) => {
//   return JSON_CURRENCY_INFO.find(({ code: theCode }) => theCode === code);
// };

export const isDarkMode = () => {
  return localStorage?.getItem?.("theme") === "dark";
};

export const setInitialMode = (mode: string) => {
  document.documentElement.classList.add(mode);
  localStorage.setItem("theme", mode);
};

export const toggleMode = () => {
  if (!isDarkMode()) {
    document.documentElement.classList.add("dark");
    localStorage.setItem("theme", "dark");
  } else {
    localStorage.setItem("theme", "light");
    document.documentElement.classList.remove("dark");
  }

  // var link = document.createElement("meta");
  // link.setAttribute("name", "theme-color");
  // if (isDarkMode()) {
  //   link.content = "#212f3c";
  // } else {
  //   link.content = "#212f3c";
  // }
  // document.getElementsByTagName("head")[0].appendChild(link);
};

export const getChartSelectionToTransactionsFormat = ({
  data,
}: {
  data: any;
}) => {
  const res = data?.reduce?.(
    (acc: any, curr: any) => {
      return {
        income: acc?.income + Number(curr?.income),
        expense: acc?.expense + Number(curr?.expenses),
      };
    },
    {
      income: 0,
      expense: 0,
    },
  );

  const { income, expense } = res;

  return [
    {
      type: ETransactionType.INCOME,
      total: income,
      status: ALL_TRANSACTION_STATUSES.PAID,
    },
    {
      type: ETransactionType.EXPENSE,
      total: expense,
      status: ALL_TRANSACTION_STATUSES.PAID,
    },
  ];
};

export const getTransactionsByType = memoize(
  ({
    type,
    account,
    transactions,
    year,
  }: {
    type: ETransactionType;
    account?: string;
    transactions: Array<any>;
    year?: any;
  }) => {
    return transactions?.filter?.(
      ({ type: documentType, account: transactionAccount, status, date }) => {
        return (
          documentType === type &&
          (account ? transactionAccount === account : true) &&
          status === ALL_TRANSACTION_STATUSES.PAID &&
          (year ? `${moment(date || "").format("YYYY")}` === `${year}` : true)
        );
      },
    );
  },
);

export const getTotalByTransactionType = memoize(
  ({
    type,
    account,
    transactions,
    year,
  }: {
    type: ETransactionType;
    account?: string;
    transactions: Array<any>;
    year?: any;
  }) => {
    let total = 0;

    transactions
      ?.filter?.(
        ({ type: documentType, account: transactionAccount, status, date }) => {
          return (
            documentType === type &&
            (account ? transactionAccount === account : true) &&
            status === ALL_TRANSACTION_STATUSES.PAID &&
            (year ? `${moment(date || "").format("YYYY")}` === `${year}` : true)
          );
        },
      )
      ?.forEach?.(({ total: nada = 0 }) => {
        total += Number(nada);
      });

    return total;
  },
);

export const getSelectionTotalsByTransactionType = memoize(
  ({
    type,
    account,
    transactions,
    year,
  }: {
    type: ETransactionType;
    account?: string;
    transactions: Array<any>;
    year?: any;
  }) => {
    let total = 0;

    transactions
      ?.filter?.(
        ({ type: documentType, account: transactionAccount, date }) => {
          return (
            documentType === type &&
            (account ? transactionAccount === account : true) &&
            (year ? `${moment(date || "").format("YYYY")}` === `${year}` : true)
          );
        },
      )
      ?.forEach?.(({ total: nada = 0 }) => {
        total += Number(nada);
      });

    return total;
  },
);

export const extractTransactionIds = memoize(
  ({ transactions }: { transactions: Array<any> }) => {
    return (
      transactions?.map?.((transaction: any) => transaction?.invoice) || []
    );
  },
);

export const getTotalByCategory = memoize(
  ({
    type,
    account,
    transactions,
    category,
  }: {
    type: ETransactionTypes;
    account?: string;
    transactions: Array<any>;
    category: string;
  }) => {
    let total = 0;

    transactions
      ?.filter?.(
        ({
          type: documentType,
          account: transactionAccount,
          category: transactionCategory,
        }) =>
          documentType === type &&
          (account ? transactionAccount === account : true) &&
          category === transactionCategory,
      )
      ?.forEach?.(({ total: nada = 0 }) => {
        total += Number(nada);
      });

    return total;
  },
);

export const getTotalByPaymentMethod = memoize(
  ({
    type,
    account,
    transactions,
    paymentMethod,
  }: {
    type: ETransactionTypes;
    account?: string;
    transactions: Array<any>;
    paymentMethod: string;
  }) => {
    let total = 0;

    transactions
      ?.filter?.(
        ({
          type: documentType,
          account: transactionAccount,
          paymentMethod: transactionMethod,
        }) =>
          documentType === type &&
          (account ? transactionAccount === account : true) &&
          (paymentMethod ? transactionMethod === paymentMethod : true),
      )
      ?.forEach?.(({ total: nada = 0 }) => {
        total += Number(nada);
      });

    return total;
  },
);

export const getTotalByPayTo = memoize(
  ({
    type,
    account,
    transactions,
    payTo,
  }: {
    type: ETransactionTypes;
    account?: string;
    transactions: Array<any>;
    payTo: string;
  }) => {
    let total = 0;

    transactions
      ?.filter?.(
        ({
          type: documentType,
          account: transactionAccount,
          payTo: transactionPayTo,
        }) =>
          documentType === type &&
          (account ? transactionAccount === account : true) &&
          (payTo ? transactionPayTo === payTo : true),
      )
      ?.forEach?.(({ total: nada = 0 }) => {
        total += Number(nada);
      });

    return total;
  },
);

export const getTotalByMonth = memoize(
  ({
    type,
    account,
    transactions,
    month,
  }: {
    type: ETransactionTypes;
    account?: string;
    transactions: Array<any>;
    month: number;
  }) => {
    let total = 0;

    transactions
      ?.filter?.(
        ({
          type: documentType,
          account: transactionAccount,
          date: transactionDate,
        }) =>
          documentType === type &&
          (account ? transactionAccount === account : true) &&
          (month ? month === new Date(transactionDate).getMonth() + 1 : true),
      )
      ?.forEach?.(({ total: nada = 0 }) => {
        total += Number(nada);
      });

    return total;
  },
);

export const getUniqueAccountsFromTransactions = memoize(
  ({ transactions }: { transactions: Array<any> }) =>
    Array.from(
      new Set(transactions?.map?.(({ account }) => account) || []),
    )?.filter?.((account) => account),
);

export const rgbaObjectToString = ({
  r,
  g,
  b,
  a,
}: {
  r: any;
  g: any;
  b: any;
  a: any;
}) => {
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

export const rgbaToHex = (color: any) => {
  const { r, g, b } = color;
  return "#" + [r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("");
};

export const getUniqueYearsFromTransactions = memoize(
  ({
    transactions,
    account = null,
  }: {
    transactions: Array<any>;
    account?: any;
  }) => {
    if (account) {
      const filtered =
        transactions?.filter?.(
          (transaction) => transaction.account === account,
        ) || [];

      return sortBy(
        Array.from(
          new Set(
            filtered?.map?.((yearDate) =>
              new Date(yearDate.date).getFullYear(),
            ) || [],
          ),
        ),
        ["value"],
      );
    }

    return sortBy(
      Array.from(
        new Set(
          transactions?.map?.((yearDate) =>
            new Date(yearDate.date).getFullYear(),
          ) || [],
        ),
      ),
      ["value"],
    );
  },
);

export const getTodayTransactionsFromTransactions = memoize(
  ({ transactions }: { transactions: Array<any> }) => {
    const today = moment().format("yyyy-MM-DD");

    return (
      transactions?.filter?.(
        (transaction) =>
          moment(transaction.date).format("yyyy-MM-DD") === today,
      ) || []
    );
  },
);

export const getSentPendingInvites = memoize(
  ({ invites, user }: { invites: Array<any>; user: string }) => {
    return (
      invites?.filter?.(
        (invite) => invite.status === "SENT" && invite.from === user,
      ) || []
    );
  },
);

export const getReceivedPendingInvites = memoize(
  ({ invites, user }: { invites: Array<any>; user: string }) => {
    return (
      invites?.filter?.(
        (invite) => invite.status === "SENT" && invite.to === user,
      ) || []
    );
  },
);

export const getAcceptedInvites = memoize(
  ({ invites, user }: { invites: Array<any>; user: string }) => {
    return (
      invites
        ?.filter?.(
          (invite) =>
            invite.status === "ACCEPTED" &&
            (invite.from === user || invite.to === user),
        )
        ?.map?.((invite) => {
          return {
            ...invite,
            iSentTheInvite: invite.to === user,
          };
        }) || []
    );
  },
);

export const getUniqueCategoriesFromTransactions = memoize(
  ({ transactions }: { transactions: Array<any> }) => {
    const categories = transactions?.reduce?.((acc, curr) => {
      // if (curr.details?.length) {
      //   acc.concat(
      //     curr.details?.map(
      //       (detail: any) => detail.category?.trim?.() || "Sin categ"
      //     )
      //   );
      // } else {
      // }
      acc.push(curr.category?.trim?.() || "Sin cate");

      return acc;
    }, []);

    return Array.from(new Set(categories))?.sort?.() || [];
  },
);

export const getUniqueDescriptionsFromTransactions = memoize(
  ({ transactions }: { transactions: Array<any> }) => {
    const descriptions = transactions?.reduce?.((acc, curr) => {
      acc.push(curr.description?.trim?.() || "Sin cate");

      return acc;
    }, []);

    return Array.from(new Set(descriptions))?.sort?.() || [];
  },
);

export const getUniquePaymentMethodsFromTransactions = memoize(
  ({ transactions }: { transactions: Array<any> }) => {
    const methods = transactions
      ?.map?.((transaction) => transaction.paymentMethod)
      ?.filter?.((method) => method);

    return Array.from(new Set(methods))?.sort?.() || [];
  },
);

export const getUniquePaidToFromTransactions = memoize(
  ({ transactions }: { transactions: Array<any> }) => {
    const paidTo = transactions
      ?.map?.((transaction) => transaction.payTo)
      ?.filter?.((payTo) => payTo);

    return Array.from(new Set(paidTo))?.sort?.() || [];
  },
);

export const getUniqueMonthsFromTransactions = memoize(
  ({ transactions }: { transactions: Array<any> }) => {
    return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  },
);

export const exportToExcel = ({
  data,
  fileName = "test",
}: {
  data: any;
  fileName?: string;
}) => {
  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
  const excelBuffer = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
  });
  const blob = new Blob([excelBuffer], {
    type: "application/octet-stream",
  });
  saveAs(blob, `${fileName}.xlsx`);
};

export const exportToJson = ({
  data,
  fileName = "test",
}: {
  data: any;
  fileName?: string;
}) => {
  const jsonStringify = JSON.stringify(data);
  var blob = new Blob([jsonStringify], { type: "application/json" });

  saveAs(blob, `${fileName}.json`);
};

export const exportToCsv = ({
  data = null,
  columnDelimiter = ",",
  lineDelimiter = "\n",
  fileName = "",
}: {
  data: any;
  columnDelimiter?: string;
  lineDelimiter?: string;
  fileName?: string;
}) => {
  let result: any;
  let ctr: any;
  let keys: any;

  if (data === null || !data.length) {
    return null;
  }

  keys = Object.keys(data[0]);

  result = "";
  result += keys.join(columnDelimiter);
  result += lineDelimiter;

  data.forEach((item: any) => {
    ctr = 0;
    keys.forEach((key: any) => {
      if (ctr > 0) {
        result += columnDelimiter;
      }

      result +=
        typeof item[key] === "string" && item[key].includes(columnDelimiter)
          ? `"${item[key]}"`
          : item[key];
      ctr++;
    });
    result += lineDelimiter;
  });

  var blob = new Blob([result], { type: "text/csv" });

  saveAs(blob, `${fileName}.csv`);

  // return result;
};

/* eslint-disable */

export const exportTransactionToPdf = ({
  transactionId,
}: {
  transactionId: any;
}) => {
  apiClient
    .get(`${API_ENDPOINT_GET_INVOICE}/${transactionId}`)
    .then((res: any) => {
      const transaction = res.data;

      if (transaction) {
        const doc = new jsPDF("p", "mm");
        doc.internal.pageSize.width = 48;
        const logo = new Image();
        logo.src = "/apple/favicon-196.png";
        logo.onload = () => {
          // doc.addImage(logo, "", 5, 5, 40, 40);

          const isInvoice = transaction?.details?.length;

          autoTable(doc, {
            margin: {
              left: 1,
              right: 1,
            },
            body: [
              [
                {
                  content: "Cali Rojas",
                  styles: {
                    halign: "left",
                    // fontSize: 16,
                    // textColor: "#ffffff",
                  },
                },
                // {
                //   content: transaction?.date,
                //   styles: {
                //     halign: "right",
                //     fontSize: 12,
                //     // textColor: "#ffffff",
                //   },
                // },
              ],
              [
                {
                  content:
                    (isInvoice ? "Factura" : "Recibo") +
                    ` #INV0001\nFecha: ${transaction?.date}`,
                  styles: {
                    halign: "left",
                  },
                },
              ],
            ],
            theme: "plain",
            styles: {
              // fillColor: "#3366ff",
            },
          });

          autoTable(doc, {
            margin: {
              left: 1,
              right: 1,
            },
            body: [
              [
                {
                  content:
                    "Facturar a:" +
                    ` ${transaction?.payTo}` +
                    "\nEscazú, San José, Costa Rica",
                  styles: {
                    halign: "left",
                  },
                },
              ],
            ],
            theme: "plain",
          });

          // autoTable(doc, {
          //   body: [
          //     [
          //       {
          //         content: "Monto a pagar:",
          //         styles: {
          //           halign: "right",
          //           fontSize: 14,
          //         },
          //       },
          //     ],
          //     [
          //       {
          //         content: `$${formatNumber(transaction?.total)}`,
          //         styles: {
          //           halign: "right",
          //           fontSize: 20,
          //           textColor: "#212f3c",
          //           fontStyle: "bold",
          //         },
          //       },
          //     ],
          //     // [
          //     //   {
          //     //     content: "Fecha de vencimiento: 2022-02-01",
          //     //     styles: {
          //     //       halign: "right",
          //     //     },
          //     //   },
          //     // ],
          //   ],
          //   theme: "plain",
          // });

          autoTable(doc, {
            margin: {
              left: 1,
              right: 1,
            },
            body: [
              [
                {
                  content: "Productos y Servicios",
                  styles: {
                    halign: "left",
                    // fontSize: 14,
                  },
                },
              ],
            ],
            theme: "plain",
          });

          if (isInvoice) {
            autoTable(doc, {
              margin: {
                left: 1,
                right: 1,
              },
              head: [["Descripción", "Cantidad", "Precio", "Total"]],
              body: transaction?.details?.map?.((row: any) => [
                row?.description || "-",
                row?.quantity || "-",
                formatNumber(row?.unitPrice || 0),
                `${formatNumber(
                  Number(row?.quantity) * Number(row?.unitPrice),
                )}`,
              ]),
              theme: "striped",
              headStyles: {
                fillColor: "#212f3c",
              },
            });
          }

          autoTable(doc, {
            margin: {
              left: 1,
              right: 1,
            },
            body: [
              [
                {
                  content: "Subtotal:",
                  styles: {
                    halign: "right",
                  },
                },
                {
                  content: `$${formatNumber(transaction?.subTotal)}`,
                  styles: {
                    halign: "right",
                    fontStyle: "bold",
                    // fontSize: 12,
                  },
                },
              ],
              [
                {
                  content: "Impuestos:",
                  styles: {
                    halign: "right",
                  },
                },
                {
                  content: `$${formatNumber(transaction?.taxes)}`,
                  styles: {
                    halign: "right",
                    fontStyle: "bold",
                    // fontSize: 12,
                  },
                },
              ],
              [
                {
                  content: "Descuento:",
                  styles: {
                    halign: "right",
                  },
                },
                {
                  content: `$${formatNumber(transaction?.discount)}`,
                  styles: {
                    halign: "right",
                    fontStyle: "bold",
                    // fontSize: 12,
                  },
                },
              ],
              [
                {
                  content: "Total:",
                  styles: {
                    halign: "right",
                  },
                },
                {
                  content: `$${formatNumber(transaction?.total)}`,
                  styles: {
                    halign: "right",
                    fontStyle: "bold",
                    // fontSize: 14,
                  },
                },
              ],
            ],
            theme: "plain",
          });

          if (transaction?.notes) {
            autoTable(doc, {
              margin: {
                left: 1,
                right: 1,
              },
              body: [
                [
                  {
                    content: "Notas",
                    styles: {
                      halign: "left",
                      // fontSize: 14,
                    },
                  },
                ],
                [
                  {
                    content: transaction?.notes,
                    styles: {
                      halign: "left",
                    },
                  },
                ],
              ],
              theme: "plain",
            });
          }

          return doc.save("invoice");
        };
      }
    });

  // autoTable(doc, {
  //   body: [
  //     [
  //       {
  //         content: "This is a centered footer",
  //         styles: {
  //           halign: "center",
  //         },
  //       },
  //     ],
  //   ],
  //   theme: "plain",
  // });
};

export const exportTransactionToPdfOriginal = async ({
  transactionId,
  share = false,
}: {
  transactionId: any;
  share?: boolean;
}) => {
  await apiClient
    .get(`${API_ENDPOINT_GET_INVOICE}/${transactionId}`)
    .then((res: any) => {
      const transaction = res.data;

      if (transaction) {
        const doc = new jsPDF("p", "pt");
        const logo = new Image();
        logo.src = "/apple/favicon-196.png";
        logo.onload = async () => {
          doc.addImage(logo, "", 40, 40, 85, 85);

          const isInvoice = transaction?.details?.length;

          autoTable(doc, {
            body: [
              [
                {
                  content: "",
                  styles: {
                    cellWidth: 90,
                  },
                },
                {
                  content: "Cali Rojas",
                  styles: {
                    halign: "left",
                    fontSize: 20,
                    // textColor: "#ffffff",
                  },
                },
                {
                  content: (isInvoice ? "Factura" : "Recibo")?.toUpperCase?.(),
                  styles: {
                    halign: "right",
                    fontSize: 20,
                    fontStyle: "bold",
                    textColor: "#ffffff",
                    fillColor: "#000",
                    cellPadding: {
                      top: 10,
                      right: 10,
                      bottom: 10,
                      left: 10,
                    },
                  },
                },
              ],
              [
                {
                  content: "",
                  styles: {
                    cellWidth: 90,
                  },
                },
                {
                  content:
                    "Teléfono: +506 1234-5678\nEmail: dev@calirojas.com\nSitio web: www.calirojas.com",
                },
                {
                  content: `${
                    isInvoice ? "Factura" : "Recibo"
                  }: #INV0001\nFecha: ${transaction?.date}`,
                  styles: {
                    halign: "right",
                  },
                },
              ],
            ],
            theme: "plain",
            styles: {
              // fillColor: "#3366ff",
            },
          });

          autoTable(doc, {
            body: [
              [
                {
                  content:
                    "Facturar a:" +
                    `\n${transaction?.payTo}` +
                    "\nEscazú, San José" +
                    "\nCosta Rica",
                  styles: {
                    halign: "left",
                    fillColor: "#f1f1f1",
                    cellPadding: {
                      top: 10,
                      right: 10,
                      bottom: 10,
                      left: 10,
                    },
                  },
                },
                {
                  content:
                    "Dirección de envío:" +
                    "\nJohn Doe" +
                    "\nShipping Address line 1" +
                    "\nShipping Address line 2" +
                    "\nZip code - City" +
                    "\nMexico",
                  styles: {
                    halign: "left",
                    fillColor: "#f1f1f1",
                    cellPadding: {
                      top: 10,
                      right: 10,
                      bottom: 10,
                      left: 10,
                    },
                  },
                },
                {
                  content:
                    "De:" +
                    "\nCali Rojas" +
                    "\nShipping Address line 1" +
                    "\nShipping Address line 2" +
                    "\nZip code - City" +
                    "\nCosta Rica",
                  styles: {
                    halign: "right",
                    fillColor: "#f1f1f1",
                    cellPadding: {
                      top: 10,
                      right: 10,
                      bottom: 10,
                      left: 10,
                    },
                  },
                },
              ],
            ],
            theme: "plain",
          });

          // autoTable(doc, {
          //   body: [
          //     [
          //       {
          //         content: "Monto a pagar:",
          //         styles: {
          //           halign: "right",
          //           fontSize: 14,
          //         },
          //       },
          //     ],
          //     [
          //       {
          //         content: `$${formatNumber(transaction?.total)}`,
          //         styles: {
          //           halign: "right",
          //           fontSize: 20,
          //           textColor: "#212f3c",
          //           fontStyle: "bold",
          //         },
          //       },
          //     ],
          //     // [
          //     //   {
          //     //     content: "Fecha de vencimiento: 2022-02-01",
          //     //     styles: {
          //     //       halign: "right",
          //     //     },
          //     //   },
          //     // ],
          //   ],
          //   theme: "plain",
          // });

          if (isInvoice) {
            autoTable(doc, {
              body: [
                [
                  {
                    content: "Productos y Servicios",
                    styles: {
                      halign: "left",
                      fontSize: 14,
                    },
                  },
                ],
              ],
              theme: "plain",
            });

            autoTable(doc, {
              head: [["Descripción", "Cantidad", "Precio", "Subtotal"]],
              body: transaction?.details?.map?.((row: any) => [
                row?.description || "-",
                row?.quantity || "-",
                formatNumber(row?.unitPrice || 0),
                `${formatNumber(
                  Number(row?.quantity) * Number(row?.unitPrice),
                )}`,
              ]),
              theme: "striped",
              headStyles: {
                fillColor: "#212f3c",
              },
            });

            autoTable(doc, {
              body: [
                [
                  {
                    content: "Subtotal:",
                    styles: {
                      halign: "right",
                    },
                  },
                  {
                    content: `$${formatNumber(transaction?.subTotal)}`,
                    styles: {
                      halign: "right",
                      fontStyle: "bold",
                      fontSize: 12,
                    },
                  },
                ],
                [
                  {
                    content: "Impuestos:",
                    styles: {
                      halign: "right",
                    },
                  },
                  {
                    content: `$${formatNumber(transaction?.taxes)}`,
                    styles: {
                      halign: "right",
                      fontStyle: "bold",
                      fontSize: 12,
                    },
                  },
                ],
                [
                  {
                    content: "Descuento:",
                    styles: {
                      halign: "right",
                    },
                  },
                  {
                    content: `$${formatNumber(transaction?.discount)}`,
                    styles: {
                      halign: "right",
                      fontStyle: "bold",
                      fontSize: 12,
                    },
                  },
                ],
                [
                  {
                    content: "Total:",
                    styles: {
                      halign: "right",
                    },
                  },
                  {
                    content: `$${formatNumber(transaction?.total)}`,
                    styles: {
                      halign: "right",
                      fontStyle: "bold",
                      fontSize: 14,
                    },
                  },
                ],
              ],
              theme: "plain",
            });
          } else {
            autoTable(doc, {
              body: [
                [
                  {
                    content: "Descripción:",
                    styles: {
                      halign: "left",
                      fontSize: 12,
                      fontStyle: "bold",
                      cellPadding: {
                        bottom: 0,
                        left: 0,
                      },
                    },
                  },
                ],
                [
                  {
                    content: transaction?.description,
                    styles: {
                      halign: "left",
                      cellPadding: {
                        top: 0,
                        left: 0,
                      },
                      fontSize: 11,
                      // fontSize: 14,
                    },
                  },
                ],
                [
                  {
                    content: "Total:",
                    styles: {
                      halign: "right",
                      fontSize: 12,
                      cellPadding: {
                        top: 10,
                        bottom: 0,
                        left: 0,
                      },
                    },
                  },
                ],
                [
                  {
                    content: `$${formatNumber(transaction?.total)}`,
                    styles: {
                      halign: "right",
                      fontStyle: "bold",
                      fontSize: 14,
                      cellPadding: {
                        top: 0,
                        left: 0,
                      },
                    },
                  },
                ],
              ],
              theme: "plain",
            });
          }

          if (transaction?.notes) {
            autoTable(doc, {
              body: [
                [
                  {
                    content: "Notas",
                    styles: {
                      halign: "left",
                      fontSize: 14,
                    },
                  },
                ],
                [
                  {
                    content: transaction?.notes,
                    styles: {
                      halign: "left",
                    },
                  },
                ],
              ],
              theme: "plain",
            });
          }

          autoTable(doc, {
            body: [
              [
                {
                  content: INVOICE_NOTES,
                  styles: {
                    halign: "center",
                  },
                },
              ],
            ],
            theme: "plain",
          });

          if (share) {
            const elBlob = new File(
              [doc.output("arraybuffer")],
              "Transaction.pdf",
              {
                type: "application/pdf",
              },
            );

            if (
              navigator.canShare({
                text: i18n.t("share.share_pdf"),
                files: [elBlob],
              })
            ) {
              await navigator
                .share({
                  text: i18n.t("share.share_pdf"),
                  files: [elBlob],
                })
                .then((a) => {})
                .catch((e) => {});
            }
          } else {
            return doc.save("invoice");
          }
        };
      }
    });
};

export const shareTransactionInPdf = async ({
  transactionId,
}: {
  transactionId: any;
}) => {
  await apiClient
    .get(`${API_ENDPOINT_GET_INVOICE}/${transactionId}`)
    .then(async (res: any) => {
      const transaction = res.data;

      const doc = new jsPDF("p", "pt");
      const logo = new Image();
      logo.src = "/apple/favicon-196.png";

      doc.addImage(logo, "", 40, 40, 85, 85);

      const isInvoice = transaction?.details?.length;

      autoTable(doc, {
        body: [
          [
            {
              content: "",
              styles: {
                cellWidth: 90,
              },
            },
            {
              content: "Cali Rojas",
              styles: {
                halign: "left",
                fontSize: 20,
                // textColor: "#ffffff",
              },
            },
            {
              content: (isInvoice ? "Factura" : "Recibo")?.toUpperCase?.(),
              styles: {
                halign: "right",
                fontSize: 20,
                fontStyle: "bold",
                textColor: "#ffffff",
                fillColor: "#000",
                cellPadding: {
                  top: 10,
                  right: 10,
                  bottom: 10,
                  left: 10,
                },
              },
            },
          ],
          [
            {
              content: "",
              styles: {
                cellWidth: 90,
              },
            },
            {
              content:
                "Teléfono: +506 1234-5678\nEmail: dev@calirojas.com\nSitio web: www.calirojas.com",
            },
            {
              content: `${isInvoice ? "Factura" : "Recibo"}: #INV0001\nFecha: ${
                transaction?.date
              }`,
              styles: {
                halign: "right",
              },
            },
          ],
        ],
        theme: "plain",
        styles: {
          // fillColor: "#3366ff",
        },
      });

      autoTable(doc, {
        body: [
          [
            {
              content:
                "Facturar a:" +
                `\n${transaction?.payTo}` +
                "\nEscazú, San José" +
                "\nCosta Rica",
              styles: {
                halign: "left",
                fillColor: "#f1f1f1",
                cellPadding: {
                  top: 10,
                  right: 10,
                  bottom: 10,
                  left: 10,
                },
              },
            },
            {
              content:
                "Dirección de envío:" +
                "\nJohn Doe" +
                "\nShipping Address line 1" +
                "\nShipping Address line 2" +
                "\nZip code - City" +
                "\nMexico",
              styles: {
                halign: "left",
                fillColor: "#f1f1f1",
                cellPadding: {
                  top: 10,
                  right: 10,
                  bottom: 10,
                  left: 10,
                },
              },
            },
            {
              content:
                "De:" +
                "\nCali Rojas" +
                "\nShipping Address line 1" +
                "\nShipping Address line 2" +
                "\nZip code - City" +
                "\nCosta Rica",
              styles: {
                halign: "right",
                fillColor: "#f1f1f1",
                cellPadding: {
                  top: 10,
                  right: 10,
                  bottom: 10,
                  left: 10,
                },
              },
            },
          ],
        ],
        theme: "plain",
      });

      // autoTable(doc, {
      //   body: [
      //     [
      //       {
      //         content: "Monto a pagar:",
      //         styles: {
      //           halign: "right",
      //           fontSize: 14,
      //         },
      //       },
      //     ],
      //     [
      //       {
      //         content: `$${formatNumber(transaction?.total)}`,
      //         styles: {
      //           halign: "right",
      //           fontSize: 20,
      //           textColor: "#212f3c",
      //           fontStyle: "bold",
      //         },
      //       },
      //     ],
      //     // [
      //     //   {
      //     //     content: "Fecha de vencimiento: 2022-02-01",
      //     //     styles: {
      //     //       halign: "right",
      //     //     },
      //     //   },
      //     // ],
      //   ],
      //   theme: "plain",
      // });

      autoTable(doc, {
        body: [
          [
            {
              content: "Productos y Servicios",
              styles: {
                halign: "left",
                fontSize: 14,
              },
            },
          ],
        ],
        theme: "plain",
      });

      if (isInvoice) {
        autoTable(doc, {
          head: [["Descripción", "Cantidad", "Precio", "Subtotal"]],
          body: transaction?.details?.map?.((row: any) => [
            row?.description || "-",
            row?.quantity || "-",
            formatNumber(row?.unitPrice || 0),
            `${formatNumber(Number(row?.quantity) * Number(row?.unitPrice))}`,
          ]),
          theme: "striped",
          headStyles: {
            fillColor: "#212f3c",
          },
        });
      }

      autoTable(doc, {
        body: [
          [
            {
              content: "Subtotal:",
              styles: {
                halign: "right",
              },
            },
            {
              content: `$${formatNumber(transaction?.subTotal)}`,
              styles: {
                halign: "right",
                fontStyle: "bold",
                fontSize: 12,
              },
            },
          ],
          [
            {
              content: "Impuestos:",
              styles: {
                halign: "right",
              },
            },
            {
              content: `$${formatNumber(transaction?.taxes)}`,
              styles: {
                halign: "right",
                fontStyle: "bold",
                fontSize: 12,
              },
            },
          ],
          [
            {
              content: "Descuento:",
              styles: {
                halign: "right",
              },
            },
            {
              content: `$${formatNumber(transaction?.discount)}`,
              styles: {
                halign: "right",
                fontStyle: "bold",
                fontSize: 12,
              },
            },
          ],
          [
            {
              content: "Total:",
              styles: {
                halign: "right",
              },
            },
            {
              content: `$${formatNumber(transaction?.total)}`,
              styles: {
                halign: "right",
                fontStyle: "bold",
                fontSize: 14,
              },
            },
          ],
        ],
        theme: "plain",
      });

      if (transaction?.notes) {
        autoTable(doc, {
          body: [
            [
              {
                content: "Notas",
                styles: {
                  halign: "left",
                  fontSize: 14,
                },
              },
            ],
            [
              {
                content: transaction?.notes,
                styles: {
                  halign: "left",
                },
              },
            ],
          ],
          theme: "plain",
        });
      }

      const elBlob = new File([doc.output("arraybuffer")], "Transaction.pdf", {
        type: "application/pdf",
      });

      if (
        navigator.canShare({
          text: i18n.t("share.share_transaction"),
          files: [elBlob],
        })
      ) {
        await navigator
          .share({
            text: i18n.t("share.share_transaction"),
            files: [elBlob],
          })
          .then((a) => {})
          .catch((e) => {});
      } else {
        return doc.output("blob");
      }
    });

  // autoTable(doc, {
  //   body: [
  //     [
  //       {
  //         content: "This is a centered footer",
  //         styles: {
  //           halign: "center",
  //         },
  //       },
  //     ],
  //   ],
  //   theme: "plain",
  // });
};

export const exportTransactionsReportToPdf = async ({
  transactions,
  fileName = "transactions",
  share = false,
}: {
  transactions: Array<any>;
  fileName?: string;
  share?: boolean;
}) => {
  const doc = new jsPDF();

  autoTable(doc, {
    body: [
      [
        {
          content: "Cali Rojas",
          styles: {
            halign: "left",
            fontSize: 20,
            // textColor: "#ffffff",
          },
        },
        {
          content: "",
          styles: {
            halign: "right",
          },
        },
        {
          content: "Transacciones".toUpperCase(),
          styles: {
            halign: "right",
            fontSize: 20,
            fontStyle: "bold",
            textColor: "#ffffff",
            fillColor: "#000",
            cellPadding: {
              top: 5,
              right: 5,
              bottom: 5,
              left: 5,
            },
            cellWidth: 80,
          },
        },
      ],
      [
        {
          content: "dev@calirojas.com",
          styles: {
            halign: "left",
          },
        },
      ],
    ],
    theme: "plain",
    styles: {
      // fillColor: "#3366ff",
    },
  });

  autoTable(doc, {
    head: [["Fecha", "Origen/Destino", "Ingreso", "Gasto", "Estado"]],
    body: transactions?.map?.((row: any) => [
      row?.date || "-",
      row?.payTo || "-",
      row?.type === ETransactionType.INCOME ? formatNumber(row?.total) : "-",
      row?.type === ETransactionType.EXPENSE ? formatNumber(row?.total) : "-",
      transactionStatus.getNameById(row?.status),
    ]),
    theme: "striped",
    headStyles: {
      fillColor: "#212f3c",
    },
  });

  // autoTable(doc, {
  //   body: [
  //     [
  //       {
  //         content: "Subtotal:",
  //         styles: {
  //           halign: "right",
  //         },
  //       },
  //       {
  //         content: `$${formatNumber(transaction?.subTotal)}`,
  //         styles: {
  //           halign: "right",
  //           fontStyle: "bold",
  //           fontSize: 12,
  //         },
  //       },
  //     ],
  //     [
  //       {
  //         content: "Impuestos:",
  //         styles: {
  //           halign: "right",
  //         },
  //       },
  //       {
  //         content: `$${formatNumber(transaction?.taxes)}`,
  //         styles: {
  //           halign: "right",
  //           fontStyle: "bold",
  //           fontSize: 12,
  //         },
  //       },
  //     ],
  //     [
  //       {
  //         content: "Descuento:",
  //         styles: {
  //           halign: "right",
  //         },
  //       },
  //       {
  //         content: `$${formatNumber(transaction?.discount)}`,
  //         styles: {
  //           halign: "right",
  //           fontStyle: "bold",
  //           fontSize: 12,
  //         },
  //       },
  //     ],
  //     [
  //       {
  //         content: "Total:",
  //         styles: {
  //           halign: "right",
  //         },
  //       },
  //       {
  //         content: `$${formatNumber(transaction?.total)}`,
  //         styles: {
  //           halign: "right",
  //           fontStyle: "bold",
  //           fontSize: 14,
  //         },
  //       },
  //     ],
  //   ],
  //   theme: "plain",
  // });
  if (share) {
    const elBlob = new File([doc.output("arraybuffer")], `${fileName}.pdf`, {
      type: "application/pdf",
    });

    if (
      navigator.canShare({
        text: i18n.t("share.share_pdf"),
        files: [elBlob],
      })
    ) {
      await navigator
        .share({
          text: i18n.t("share.share_pdf"),
          files: [elBlob],
        })
        .then((a) => {})
        .catch((e) => {});
    }
  } else {
    return doc.save(fileName);
  }
};

export const isPwa = () => {
  return ["fullscreen", "standalone", "minimal-ui"].some(
    (displayMode) => matchMedia(`(display-mode: ${displayMode})`).matches,
  );
};

export const showNativeNotification = async ({
  message,
  kind = "info",
  title = "",
  id = "",
}: {
  message: string;
  kind: "info" | "error" | "success";
  title?: string;
  id?: string;
}) => {
  const leTitle = isPwa() ? "Notificación" : "eXpenser";
  const registration = await navigator.serviceWorker?.getRegistration?.();
  const icon = "/apple/favicon-196.png";

  if (Notification.permission === "granted") {
    if (registration?.active && "showNotification" in registration) {
      registration.showNotification(title || leTitle, {
        body: message,
        icon,
        ...(id ? { tag: id } : {}),
        // requireInteraction: true,
      });
    } else {
      new Notification(title || leTitle, {
        body: message,
        icon,
        ...(id ? { tag: id } : {}),
        // requireInteraction: true,
      });
    }
  } else {
    if (kind !== "info") {
      toast[kind](message);
    } else {
      toast(message);
    }
  }
};

export const myNotification = ({
  title,
  options,
}: {
  title: string;
  options: any;
}) => {
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.ready
      .then(async (registration) => {
        await registration.showNotification(title, options);
      })
      .catch((error) => {
        console.error("El Service Worker no está listo:", error);
      });
  } else {
    console.error("El Service Worker no está disponible.");
  }
};

export const showErrorNotification = ({
  message,
  kind = "error",
  title = "",
  id = "",
}: {
  message: string;
  kind: "info" | "error" | "success";
  title?: string;
  id?: string;
}) => {
  if ("setAppBadge" in navigator) {
    (navigator as any).setAppBadge(1);
  }
  showNativeNotification({ message, kind, title, id });
};

export const setAppBadge = async ({ value }: { value: string }) => {
  if ("setAppBadge" in navigator) {
    await (navigator as any).setAppBadge(value);
  }
};

export const clearAppBadge = async () => {
  if ("clearAppBadge" in navigator) {
    await (navigator as any).clearAppBadge();
  }
};

export const createIcon = memoize((icon: any, options: any) =>
  createElement(icon, options),
);

/* eslint-enable */
export const showNotification = ({
  kind = "info",
  message,
}: {
  kind: "info" | "error" | "success";
  message: string;
}) => {
  if (kind !== "info") {
    switch (kind) {
      case "error":
        toast[kind](message, {
          icon: createIcon(MdClose, {
            size: ICON_SIZES.SM,
            className: "text-red-400",
          }),
        });

        break;
      case "success":
        toast[kind](message, {
          icon: createIcon(MdCheck, {
            size: ICON_SIZES.SM,
            className: "text-lime-500",
          }),
        });
        break;

      default: {
      }
    }
  } else {
    toast(message);
  }
};

export const getShortName = memoize((name: string) => {
  if (name) {
    const [shortName] = name?.split(" ");

    return shortName;
  }

  return name;
});

export const getFirstAndLastDayOfMonth = memoize(
  ({ year, month }: { year: any; month: any }) => {
    const leMonth = month > 9 ? month : `0${month}`;
    const firstDay = moment(`${year}-${leMonth}-01`)
      ?.startOf?.("month")
      ?.format?.("yyyy-MM-DD");
    const lastDay = moment(`${year}-${leMonth}-01`)
      ?.endOf?.("month")
      ?.format?.("yyyy-MM-DD");

    return {
      firstDay,
      lastDay,
    };
  },
);

export const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export const getCountryDataByCode = memoize(({ code }: { code: string }) => {
  return JSON_COUNTRY_INFO?.find?.(
    ({ code: countryCode }) => code === countryCode,
  );
});

export const getAllTimezones = () => {
  return moment.tz.names();
};

export const getPaginationData = memoize(
  ({
    data,
    settings: { recordsPerPage = 10, currentPage = 1 },
  }: {
    data: Array<any>;
    settings: any;
  }) => {
    const totalRecords = data?.length;
    const totalPages = Math.ceil(totalRecords / recordsPerPage);

    if (totalRecords > recordsPerPage) {
      return {
        records: take(data || [], currentPage * recordsPerPage),
        hasNextPage: currentPage + 1 <= totalPages,
        hasPreviousPage: currentPage > 1,
      };
    }

    return {
      records: data,
      hasNextPage: false,
      hasPreviousPage: false,
    };
  },
);

export const generatePassword = () => {
  const chars =
    "0123456789abcdefghijklmnopqrstuvwxyz!@#$%^&*()ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const passwordLength = 20;
  let password = "";
  for (var i = 0; i <= passwordLength; i++) {
    var randomNumber = Math.floor(Math.random() * chars.length);
    password += chars.substring(randomNumber, randomNumber + 1);
  }

  return password;
};

export const formatTime = ({
  timeToFormat,
  format = "hh:mm A",
}: {
  timeToFormat: string;
  format?: string;
}) => {
  if (!timeToFormat) return "--:--";

  return moment(timeToFormat, ["HH:mm", "H:m", "HH:mm:ss"], true)
    .locale(currentLanguage())
    .format(format);
};

export const formatEasyDate = ({
  dateToFormat,
  format = "yyyy-MM-DD",
}: {
  dateToFormat: string;
  format?: string;
}) => {
  const currentDate = moment().format(format);

  switch (dateToFormat) {
    case currentDate: {
      return i18n.t("dates:days.today");
    }
    case moment(currentDate).subtract(1, "day").format(format): {
      return i18n.t("dates:days.yesterday");
    }
    case moment(currentDate).subtract(2, "day").format(format): {
      return i18n.t("dates:days.before_yesterday");
    }

    default: {
      return moment(dateToFormat).format(format);
    }
  }
};

export const isEasyDate = (date: string) => {
  const m = moment(date);

  if (!m.isValid()) return false;

  const today = moment();

  return (
    m.isSame(today, "day") ||
    m.isSame(moment().subtract(1, "day"), "day") ||
    m.isSame(moment().subtract(2, "day"), "day")
  );
};

export const formatFileSize = (size: number) => {
  return size < 1024 * 1024
    ? (size / 1024).toFixed(2) + " KB"
    : (size / (1024 * 1024)).toFixed(2) + " MB";
};

export const getFileType = (file: unknown): string => {
  if (!file) return "";

  if (isObject(file)) {
    const type = get(file, "type", "");
    return (type as any) === "N/A"
      ? ""
      : type?.split?.("/")?.pop?.()?.toUpperCase?.() || "";
  }

  if (typeof file === "string") {
    const fileStr = file as string;
    if (fileStr.includes(".")) {
      return fileStr.split(".").pop()?.toUpperCase() || "";
    }
  }

  return "";
};

export const getFileInfo = (file: unknown) => {
  const DEFAULT_VALUE = "Desconocido";

  const template = {
    name: DEFAULT_VALUE,
    size: 0,
    formattedSize: "0 KB",
    fileUrl: null as string | null,
    formattedType: getFileType(file) || DEFAULT_VALUE,
    thumbnailUrl: null as string | null,
  };

  if (isObject(file)) {
    if (template.formattedType === DEFAULT_VALUE) {
      return {
        ...template,
        thumbnailUrl: "",
        fileUrl: "",
      };
    }

    const name = get(file, "originalName", DEFAULT_VALUE);
    const size = get(file, "size", 0);
    const fileUrl = get(file, "fileUrl", null);
    const thumbnailUrl = get(file, "thumbnailUrl", null);

    return {
      ...template,
      name,
      size,
      formattedSize: formatFileSize(size),
      fileUrl,
      thumbnailUrl,
    };
  }

  if (isString(file)) {
    return {
      ...template,
      name: file,
      fileUrl: `${FILE_STORAGE_SERVER_URL}/${file}`,
      thumbnailUrl: `${FILE_STORAGE_SERVER_URL}/thumbnail-${file}`,
    };
  }

  return template;
};

export const isEven = (value: number) => {
  return value % 2 === 0;
};

export const formatLongDate = (date: string, excludeCurrentYear?: boolean) => {
  const cleaned = date.replace("tt", "").trim();

  const m = moment(cleaned).locale(currentLanguage());
  const dayName = m.format("dddd");
  const dayNumber = m.format("D");
  const monthName = m.format("MMMM");
  const yearNumber = m.format("YYYY");

  const currentYear = moment().format("YYYY");

  const shouldExcludeYear = excludeCurrentYear && yearNumber === currentYear;

  const capitalize = (str: string) =>
    str.charAt(0).toUpperCase() + str.slice(1);

  switch (currentLanguage()) {
    case "en":
      return capitalizeTitle(
        shouldExcludeYear
          ? m.format("dddd, MMMM D")
          : m.format("dddd, MMMM D, YYYY"),
      );

    case "es":
    case "pt":
      return capitalizeTitle(
        shouldExcludeYear
          ? `${capitalize(dayName)} ${dayNumber} de ${capitalize(monthName)}`
          : `${capitalize(dayName)} ${dayNumber} de ${capitalize(monthName)} de ${yearNumber}`,
      );

    case "zh":
      return shouldExcludeYear
        ? `${monthName}${dayNumber}日（${dayName}）`
        : `${yearNumber}年${monthName}${dayNumber}日（${dayName}）`;

    case "hi":
      return shouldExcludeYear
        ? `${dayNumber} ${monthName}`
        : `${dayNumber} ${monthName} ${yearNumber}`;

    default:
      return capitalizeTitle(
        shouldExcludeYear
          ? m.format("dddd, MMMM D")
          : m.format("dddd, MMMM D, YYYY"),
      );
  }
};

export const transactionDescription = memoize((transaction: any) => {
  if (transaction?.description) {
    return transaction?.description;
  }

  if (transaction?.details?.length) {
    if (transaction?.details?.length === 1) {
      return transaction?.details?.[0]?.description;
    }

    return "Transacción con múltiples líneas";
  }

  return "Sin descripción";
});

export const calculateNextPaymentDate = (
  fromDate: string | moment.Moment,
  frequency: ERecurringTransactionFrequency,
  customInterval?: string,
): string => {
  const base = moment(fromDate).startOf("day");

  switch (frequency) {
    case ERecurringTransactionFrequency.WEEKLY:
      return base.add(1, "week").format("YYYY-MM-DD");
    case ERecurringTransactionFrequency.FORTNIGHT:
      return base.add(14, "days").format("YYYY-MM-DD");
    case ERecurringTransactionFrequency.MONTHLY:
      return base.add(1, "month").format("YYYY-MM-DD");
    case ERecurringTransactionFrequency.QUARTER:
      return base.add(3, "months").format("YYYY-MM-DD");
    case ERecurringTransactionFrequency.BIANNUAL:
      return base.add(6, "months").format("YYYY-MM-DD");
    case ERecurringTransactionFrequency.ANNUAL:
      return base.add(1, "year").format("YYYY-MM-DD");
    case ERecurringTransactionFrequency.CUSTOM:
      return base
        .add(Number(customInterval || 30), "days")
        .format("YYYY-MM-DD");
    default:
      return base.format("YYYY-MM-DD");
  }
};

export const getFormattedLink = ({
  value,
  type,
}: {
  value: string;
  type: EContactType;
}): string => {
  switch (type) {
    case EContactType.EMAIL: {
      return `mailto:${value}`;
    }
    case EContactType.SMS: {
      return `sms:${value}`;
    }
    case EContactType.PHONE: {
      return `tel:${value}`;
    }
    default: {
      return value;
    }
  }
};

// Función para filtrar transacciones activas
export const filterTransactions = (transactions: any, filter: any) =>
  transactions?.filter?.((transaction: any) => {
    const { archived, shared, deleted, status, type } = transaction;
    return (
      (filter.archived === undefined || archived === filter.archived) &&
      (filter.shared === undefined || shared === filter.shared) &&
      (filter.deleted === undefined || deleted === filter.deleted) &&
      (filter.status === undefined || status === filter.status) &&
      (filter.type === undefined || type === filter.type)
    );
  }) || [];

export const urlBase64ToUint8Array = (base64String: string) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, "+")
    .replace(/_/g, "/");

  const rawData = atob(base64);
  return new Uint8Array([...rawData].map((char) => char.charCodeAt(0)));
};

export const objectHasChanges = ({
  original,
  edited,
}: {
  original: any;
  edited: any;
}) => {
  return !isEqual(original, edited);
};

export const getIconSymbolUrl = (icon: ReactElement): string => {
  const rawSvg = renderToStaticMarkup(icon);

  // Codifica caracteres especiales para uso seguro en URL
  const encoded = encodeURIComponent(rawSvg)
    .replace(/'/g, "%27")
    .replace(/"/g, "%22");

  return `image://data:image/svg+xml;utf8,${encoded}`;
};

export const openExternalLink = (url: string) => {
  const link = document.createElement("a");
  link.target = "_blank";
  link.href = url;
  link.style.display = "none";
  link.click();
  link?.remove?.();
};

export const iconToBase64 = (icon: ReactNode) => {
  const markup = renderToStaticMarkup(icon);

  return `data:image/svg+xml;base64,${btoa(markup)}`;
};

export const truncateWithEllipsis = ({
  text,
  maxLen = 50,
}: {
  text: string;
  maxLen?: number;
}) => {
  if (typeof text !== "string") return "";

  const trimmed = text.trimEnd();

  if (trimmed.length <= maxLen) return trimmed;

  const sliced = trimmed.slice(0, maxLen).trimEnd();

  if (sliced.endsWith(".")) return sliced;

  return sliced + "...";
};

export const getMimeTypes = (fileTypeIds: string[]): string => {
  const types = _.filter(SUPPORTED_FILE_TYPES, (type) =>
    fileTypeIds.includes(type.id),
  );

  const mimeTypes = _.flatMap(types, "mimeTypes");

  return _.uniq(mimeTypes).join(", ");
};

export const getFileFormatsSummary = (files: any[]) => {
  const grouped = groupBy(files, (f) => f.formattedType?.toUpperCase());

  return map(SUPPORTED_FILE_TYPES, (type) => {
    const formatFiles = grouped[type.name] || [];
    return {
      format: type.id,
      name: type.name,
      icon: type.icon,
      count: formatFiles.length,
    };
  }).filter((item) => item.count > 0);
};

export const getFileTypeByFilename = (filename: string) => {
  const defaultInfo = {
    id: "UNKNOWN",
    name: "Unknown",
    mimeTypes: [],
    extensions: [],
    icon: "MdPictureAsPdf",
  };
  if (!filename || typeof filename !== "string") return defaultInfo;

  const ext = `.${toLower(last(filename.split(".")))}`;

  return (
    find(SUPPORTED_FILE_TYPES, (type) =>
      some(type.extensions, (e) => toLower(e) === ext),
    ) || defaultInfo
  );
};

const escapeVCard = (value?: string) => {
  if (!value) return "";
  return value
    .replace(/\\/g, "\\\\")
    .replace(/\n/g, "\\n")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,");
};

export const getQrCodeDataByUser = (userInfo: any) => {
  if (!userInfo) return "";

  const countryData = getCountryDataByCode(userInfo?.country);

  return [
    "BEGIN:VCARD",
    "VERSION:3.0",
    `FN:${escapeVCard(userInfo?.name)}`,
    `EMAIL:${escapeVCard(userInfo?.email)}`,
    `TEL;TYPE=CELL:${escapeVCard(userInfo?.mobile)}`,
    ...(userInfo?.intro ? [`NOTE:${escapeVCard(userInfo.intro)}`] : []),
    "URL:https://expenser.net",
    ...(userInfo?.timezone ? [`TZ:${escapeVCard(userInfo.timezone)}`] : []),
    `ADR:;;${countryData?.name ? escapeVCard(countryData.name) : ""};;;;`,
    "END:VCARD",
  ].join("\r\n");
};

export const getAccountsByCurrency = ({
  accounts,
  currencyCode,
}: {
  accounts: Array<any>;
  currencyCode: string;
}) => {
  if (!accounts?.length) return [];

  return accounts.filter((account: any) => account?.currency === currencyCode);
};

export const addTooltipAttributes = ({
  id,
  content,
  isMobile,
}: {
  id: string;
  content: string;
  isMobile: boolean;
}): Record<string, string | number> => {
  return {
    "data-tooltip-id": id,
    "data-tooltip-content": isMobile ? "" : content,
    "data-tooltip-position-strategy": "fixed",
    "data-tooltip-open-on": "hover",
    "data-tooltip-offset": 0,
  };
};

export const removeUndefined = (obj: any) => {
  return omitBy(obj, isUndefined);
};

export const ensureDot = (text: string): string => {
  if (_.isEmpty(_.trim(text))) return text;
  return _.endsWith(text, ".") ? text : `${text}.`;
};

export const getUniqueImageMimeTypes = () => {
  return Array.from(
    new Set(
      SUPPORTED_FILE_TYPES.filter((t: any) => t.isImage).flatMap(
        (t: any) => t.mimeTypes,
      ),
    ),
  );
};
